import { useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import SaveIcon from '@mui/icons-material/Save';
import { CircularProgress, TextField, Tooltip } from '@mui/material';
import { openPopUp } from '@containers/App/containers/PopUp/actions';
import POPUP_TYPES from '@containers/App/containers/PopUp/types';
import { updateStaffUserName } from '@containers/Admin/containers/StaffPage/actions';
import { useTranslation } from 'react-i18next';
import {
  EditorInputGroup,
  EditorSaveAction,
  EditorSaveButton,
  EditorSubLabel,
  NameEditorFields,
} from '../../styles';

export const EditUserName = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const userData = useSelector((state: RootStateOrAny) => state.getIn(['staffPage', 'userData']));
  const saving = useSelector((state: RootStateOrAny) => state.getIn(['staffPage', 'loading']));
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  useEffect(() => {
    setFirstName(userData?.firstName || '');
    setLastName(userData?.lastName || '');
  }, [userData?.firstName, userData?.lastName]);

  const oldName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim();
  const newName = `${firstName.trim()} ${lastName.trim()}`.trim();
  const disabled = Boolean(saving) || !firstName.trim() || !lastName.trim() || oldName === newName;

  const confirmUpdate = () => {
    if (disabled || !userData?.id) return;
    dispatch(openPopUp(POPUP_TYPES.CONFIRM, {
      onConfirm: (confirm: boolean) => confirm && dispatch(updateStaffUserName(
        userData.id,
        firstName.trim(),
        lastName.trim(),
      )),
      titleText: t('Admin.User.nameChangeTitle'),
      bodyText: t('Admin.User.confirmNameUpdate', { oldName, newName }),
      confirmText: t('Actions.Simple.yes'),
      cancelText: t('Actions.Simple.no'),
      disableBackdrop: true,
    }));
  };

  const saveOnEnter = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') confirmUpdate();
  };

  return (
    <NameEditorFields>
      <EditorInputGroup>
        <EditorSubLabel>{t('Informative.Labels.firstName')}</EditorSubLabel>
        <TextField
          size="small"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          onKeyDown={saveOnEnter}
          disabled={Boolean(saving)}
        />
      </EditorInputGroup>
      <EditorInputGroup>
        <EditorSubLabel>{t('Informative.Labels.lastName')}</EditorSubLabel>
        <TextField
          size="small"
          value={lastName}
          onChange={(event) => setLastName(event.target.value)}
          onKeyDown={saveOnEnter}
          disabled={Boolean(saving)}
        />
      </EditorInputGroup>
      <Tooltip title={t('Actions.Instance.save')} arrow>
        <EditorSaveAction className="multi-row">
          <EditorSaveButton
            type="button"
            aria-label={t('Actions.Instance.save')}
            disabled={disabled}
            onClick={confirmUpdate}
          >
            {saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
          </EditorSaveButton>
        </EditorSaveAction>
      </Tooltip>
    </NameEditorFields>
  );
};